import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { ElevationData, ElevationLocation, LatLngType } from "@repo/ping-react-js";

export type MeasurementToolKind = "dtc" | "area" | "line" | "elevation" | "elevation-profile";

export type MeasurementToolCapture = {
  tool: MeasurementToolKind;
  label: string;
  value: string;
  points: { lat: number; lng: number }[];
  // elevation-profile only — the chart's series, in the units it's already displayed in.
  series?: { distance_ft: number; elevation_ft: number }[];
};

type MeasurementsInitialState = {
  hasElevationPoints: boolean;
  hasLineMeasurement: boolean;
  hasElevationProfile: boolean;
  hasAreaMeasurement: boolean;
  elevationProfileMode: boolean;
  elevationState: ElevationData | null;
  elevationProfile: ElevationLocation[];
  dtcMode: boolean;
  polygonalMeasurementMode: boolean;
  lineMeasurementMode: boolean;
  elevationMeasurementMode: boolean;
  measurementToolActive: boolean;
  toolCaptures: Record<MeasurementToolKind, MeasurementToolCapture | null>;
};

const initialState: MeasurementsInitialState = {
  hasElevationPoints: false,
  hasLineMeasurement: false,
  hasElevationProfile: false,
  hasAreaMeasurement: false,
  elevationProfileMode: false,
  elevationState: null,
  elevationProfile: [],
  dtcMode: false,
  polygonalMeasurementMode: false,
  lineMeasurementMode: false,
  elevationMeasurementMode: false,
  measurementToolActive: false,
  toolCaptures: { dtc: null, area: null, line: null, elevation: null, "elevation-profile": null },
};

const measurementsSlice = createSlice({
  name: "measurements",
  initialState,
  reducers: {
    setHasElevationPoints(state, action: PayloadAction<boolean>) {
      state.hasElevationPoints = action.payload;
    },
    setHasLineMeasurement(state, action: PayloadAction<boolean>) {
      state.hasLineMeasurement = action.payload;
    },
    setHasElevationProfile(state, action: PayloadAction<boolean>) {
      state.hasElevationProfile = action.payload;
    },
    setHasAreaMeasurement(state, action: PayloadAction<boolean>) {
      state.hasAreaMeasurement = action.payload;
    },
    setElevationProfileMode(state, action: PayloadAction<boolean>) {
      state.elevationProfileMode = action.payload;
    },
    setElevationStateData(state, action: PayloadAction<ElevationData>) {
      state.elevationState = action.payload;
    },
    setElevationLatLng(state, action: PayloadAction<LatLngType>) {
      state.elevationState = {
        ...state.elevationState,
        point: { lat: action.payload.lat, lng: action.payload.lng },
      };
    },
    setElevationProfile(state, action: PayloadAction<any[]>) {
      state.elevationProfile = action.payload;
    },
    setDTCMode(state, action: PayloadAction<boolean>) {
      state.dtcMode = action.payload;
    },
    setPolygonalMeasurementMode(state, action: PayloadAction<boolean>) {
      state.polygonalMeasurementMode = action.payload;
    },
    setLineMeasurementMode(state, action: PayloadAction<boolean>) {
      state.lineMeasurementMode = action.payload;
    },
    setElevationMeasurementMode(state, action: PayloadAction<boolean>) {
      state.elevationMeasurementMode = action.payload;
    },
    setMeasurementToolActive(state, action: PayloadAction<boolean>) {
      state.measurementToolActive = action.payload;
    },
    setToolCapture(
      state,
      action: PayloadAction<{ tool: MeasurementToolKind; capture: MeasurementToolCapture | null }>,
    ) {
      state.toolCaptures[action.payload.tool] = action.payload.capture;
    },
  },
});

const { actions, reducer } = measurementsSlice;

export const {
  setHasElevationPoints,
  setHasLineMeasurement,
  setHasElevationProfile,
  setHasAreaMeasurement,
  setElevationProfileMode,
  setElevationStateData,
  setElevationProfile,
  setDTCMode,
  setPolygonalMeasurementMode,
  setLineMeasurementMode,
  setElevationMeasurementMode,
  setMeasurementToolActive,
  setToolCapture,
} = actions;

export default reducer;
